/**
 * Entity view service: dim-ui-entity's queries, read through this app's SPARQL endpoint and the
 * public /describe endpoint, and the source its hooks read with.
 */

import {
  entityEndpoints,
  countIncoming as countIncomingIn,
  lineStops as lineStopsIn,
  linkedOfKind as linkedOfKindIn,
  listIncoming as listIncomingIn,
  loadViewShapes as loadViewShapesFrom,
  peerContext as peerContextIn,
  resolvePropertyValues as resolvePropertyValuesIn,
  resolveTable as resolveTableIn,
  type EntitySource,
  type IncomingGroup,
  type QualityReport,
  type KmWindow,
  type ViewPropertyShape,
} from '@bn-apps/dim-ui-entity/model';
import config from '@config/env';
import sparqlService, { describeEntity } from '@services/sparql.service';
import { rinfStatusFileUrl } from '@utils/rinfStatus.utils';
import type { EntityProperty } from '@/types';

export {
  VALUE_LIMIT,
  buildTableQuery,
  buildValuesQuery,
  type IncomingGroup,
  type KmWindow,
  type LineStop,
  type LinkedItem,
  type PeerContext,
  type ResolvedValues,
  type SubjectValues,
  type TableItem,
  type TableValues,
} from '@bn-apps/dim-ui-entity/model';

/** The entity page shapes of dim-semantics, mounted next to the search config. */
export const VIEW_SHAPES_URL = '/semantics-config/shacl-view.ttl';

/** The report file on this machine: only the dev server serves it (server/validation-report-dev.ts). */
async function fromDevFile(focus: string[]): Promise<QualityReport | null> {
  const response = await fetch('/dev-api/validation-results', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ focus }),
  });
  const isJson = response.headers.get('Content-Type')?.includes('json') ?? false;
  return response.ok && isJson ? response.json() : null;
}

export const entitySource: EntitySource = {
  select: (query) => sparqlService.query(query),
  describe: describeEntity,
  endpoints: { ...entityEndpoints(), viewShapes: VIEW_SHAPES_URL, rinfStatus: rinfStatusFileUrl() },
  qualityFallback: config.isDevelopment ? fromDevFile : undefined,
};

const source = entitySource;

export const loadViewShapes = () => loadViewShapesFrom(VIEW_SHAPES_URL);

export const resolvePropertyValues = (
  subjects: string[],
  properties: ViewPropertyShape[],
  known?: Record<string, EntityProperty[]>
) => resolvePropertyValuesIn(source, subjects, properties, known);

export const resolveTable = (
  subject: string,
  property: ViewPropertyShape,
  columns: ViewPropertyShape[],
  limit: number,
  window?: KmWindow
) => resolveTableIn(source, subject, property, columns, limit, window);

export const countIncoming = (uri: string) => countIncomingIn(source, uri);

export const listIncoming = (
  uri: string,
  group: Pick<IncomingGroup, 'predicate' | 'classIri'>,
  offset: number,
  pageSize: number
) => listIncomingIn(source, uri, group, offset, pageSize);

export const peerContext = (classIri: string, predicate: string, parent: string) =>
  peerContextIn(source, classIri, predicate, parent);

export const lineStops = (line: string) => lineStopsIn(source, line);

export const linkedOfKind = (subject: string, predicate: string, kind: string, limit?: number) =>
  linkedOfKindIn(source, subject, predicate, kind, limit);
